import { useCallback, useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import { useGroups } from "./hooks/useGroups";
import type { ContactGroup } from "./types";
import {
  ContextMenu,
  Icon,
  PageSectionHeader,
} from "../../components/ui";
import type { ContextMenuEntry } from "../../components/ui/ContextMenu";
import { useContextMenu } from "../../components/ui/useContextMenu";
import { MarkdownEditor } from "../../components/shared/MarkdownEditor";
import { useEditorMentionSuggestion } from "../../components/shared/markdown/useEditorMentionSuggestion";

// ── Icon + Color picker popover ─────────────────────────────────────────────

const COLORS = [
  "#FF8400",
  "#8B5CF6",
  "#3B82F6",
  "#22C55E",
  "#F59E0B",
  "#EF4444",
  "#EC4899",
  "#06B6D4",
];

function IconColorPicker({
  color,
  onColorChange,
}: {
  readonly color: string;
  readonly onColorChange: (color: string) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handle);
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => { document.removeEventListener("mousedown", handle); };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => { setOpen(!open); }}
        className="w-7 h-7 rounded-full flex items-center justify-center cursor-pointer shrink-0 transition-transform hover:scale-105"
        style={{ backgroundColor: color }}
      >
        <Icon name="users" size={13} className="text-white" />
      </button>
      {open && (
        <div className="absolute left-0 top-11 z-10 p-2 rounded-lg bg-surface-tertiary border border-edge shadow-lg space-y-2">
          <div className="flex gap-1.5">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => {
                  onColorChange(c);
                  setOpen(false);
                }}
                className={`w-6 h-6 rounded-full cursor-pointer transition-all ${
                  color === c
                    ? "ring-2 ring-white/50 scale-110"
                    : "opacity-70 hover:opacity-100"
                }`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Group list row ──────────────────────────────────────────────────────────

function GroupRow({
  group,
  onClick,
}: {
  readonly group: ContactGroup;
  readonly onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center justify-between px-4 py-3.5 text-left cursor-pointer hover:bg-surface-hover transition-colors"
    >
      <div className="flex items-center gap-3 min-w-0">
        <span
          className="w-3 h-3 rounded-full shrink-0"
          style={{ backgroundColor: group.color }}
        />
        <span className="text-sm text-content truncate">{group.name}</span>
      </div>
      <Icon name="chevron-right" size={18} className="text-content-muted" />
    </button>
  );
}

// ── Three-dot menu items ────────────────────────────────────────────────────

const GROUP_MENU_ITEMS: readonly ContextMenuEntry[] = [
  { id: "delete", label: "Delete Group", icon: "trash", variant: "danger" },
];

// ── Group detail view ───────────────────────────────────────────────────────

function GroupDetail({
  group,
  onBack,
  onUpdate,
  onDelete,
}: {
  readonly group: ContactGroup;
  readonly onBack: () => void;
  readonly onUpdate: (
    id: string,
    updates: {
      name?: string;
      color?: string;
      description?: string;
      memory?: string;
    },
  ) => Promise<void>;
  readonly onDelete: (id: string) => Promise<void>;
}): JSX.Element {
  const [name, setName] = useState(group.name);
  const [color, setColor] = useState(group.color);
  const [description, setDescription] = useState(group.description);
  const [mode, setMode] = useState<"wysiwyg" | "markdown">("wysiwyg");
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const nameInputRef = useRef<HTMLInputElement>(null);
  const memoryRef = useRef(group.memory);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;
  const mentionSuggestion = useEditorMentionSuggestion();

  const menu = useContextMenu<null>();

  useEffect(() => {
    setName(group.name);
    setColor(group.color);
    setDescription(group.description);
    memoryRef.current = group.memory;
  }, [group.id, group.name, group.color, group.description, group.memory]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const save = useCallback(
    (updates: {
      name?: string;
      color?: string;
      description?: string;
      memory?: string;
    }) => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        void onUpdateRef.current(group.id, updates);
      }, 500);
    },
    [group.id],
  );

  const handleBack = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      void onUpdateRef.current(group.id, {
        name,
        color,
        description,
        memory: memoryRef.current,
      });
    }
    onBack();
  }, [group.id, name, color, description, onBack]);

  const handleNameClick = useCallback(() => {
    setNameDraft(name);
    setEditingName(true);
    requestAnimationFrame(() => nameInputRef.current?.select());
  }, [name]);

  const handleNameSubmit = useCallback(() => {
    setEditingName(false);
    const newName = nameDraft.trim() || "Untitled";
    if (newName !== name) {
      setName(newName);
      save({ name: newName });
    }
  }, [nameDraft, name, save]);

  const handleNameKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") handleNameSubmit();
      if (e.key === "Escape") setEditingName(false);
    },
    [handleNameSubmit],
  );

  const handleColorChange = useCallback(
    (c: string) => {
      setColor(c);
      save({ color: c });
    },
    [save],
  );

  const handleDescriptionBlur = useCallback(() => {
    if (description !== group.description) save({ description });
  }, [description, group.description, save]);

  const toggleMode = useCallback(() => {
    setMode((m) => (m === "wysiwyg" ? "markdown" : "wysiwyg"));
  }, []);

  const handleMenuSelect = useCallback(
    (actionId: string) => {
      menu.close();
      if (actionId === "delete") {
        void onDelete(group.id).then(onBack);
      }
    },
    [group.id, onDelete, onBack, menu],
  );

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-edge shrink-0">
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          <button
            type="button"
            onClick={handleBack}
            className="cursor-pointer shrink-0"
          >
            <Icon name="chevron-left" size={20} className="text-accent" />
          </button>
          <IconColorPicker color={color} onColorChange={handleColorChange} />
          <div className="flex-1 min-w-0">
            {editingName ? (
              <input
                ref={nameInputRef}
                className="w-full bg-transparent text-content text-[15px] font-semibold outline-none"
                value={nameDraft}
                onChange={(e) => { setNameDraft(e.target.value); }}
                onBlur={handleNameSubmit}
                onKeyDown={handleNameKeyDown}
              />
            ) : (
              <div
                className="text-[15px] font-semibold text-content truncate cursor-text"
                onClick={handleNameClick}
              >
                {name}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <div
            className="cursor-pointer p-1.5 rounded-lg hover:bg-surface-hover"
            onClick={(e) => { menu.open(e, null); }}
          >
            <Icon name="ellipsis-vertical" size={15} />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-5 space-y-4">
        {/* Description */}
        <div>
          <span className="text-[11px] font-medium text-content-tertiary uppercase tracking-wide">
            Description
          </span>
          <input
            type="text"
            value={description}
            onChange={(e) => { setDescription(e.target.value); }}
            onBlur={handleDescriptionBlur}
            placeholder="Brief description of this group..."
            className="w-full mt-1 px-3 py-2 rounded-lg bg-surface-secondary border border-edge text-sm text-content focus:border-accent focus:outline-none"
          />
        </div>

        {/* Memory */}
        <div>
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium text-content-tertiary uppercase tracking-wide">
              Memory
            </span>
            <button
              type="button"
              onClick={toggleMode}
              className="flex items-center gap-1 text-[11px] text-content-muted hover:text-content-secondary cursor-pointer"
            >
              <Icon name={mode === "wysiwyg" ? "code" : "edit"} size={12} />
              {mode === "wysiwyg" ? "Markdown" : "Editor"}
            </button>
          </div>
          <div className="mt-1 rounded-lg bg-surface-secondary border border-edge overflow-hidden min-h-[300px]">
            {mode === "wysiwyg" ? (
              <MarkdownEditor
                initialValue={group.memory}
                onChange={(v) => {
                  memoryRef.current = v;
                  save({ memory: v });
                }}
                placeholder="Group rules, policies, tone of voice..."
                mentionSuggestion={mentionSuggestion}
              />
            ) : (
              <textarea
                className="w-full min-h-[300px] bg-transparent text-content text-sm font-mono p-3 outline-none resize-y"
                value={memoryRef.current}
                onChange={(e) => {
                  memoryRef.current = e.target.value;
                  save({ memory: e.target.value });
                }}
                placeholder="Group rules, policies, tone of voice..."
              />
            )}
          </div>
        </div>
      </div>

      {/* Three-dot context menu */}
      {menu.state.isOpen && (
        <ContextMenu
          items={GROUP_MENU_ITEMS}
          position={menu.state.position}
          onSelect={handleMenuSelect}
          onClose={menu.close}
        />
      )}
    </div>
  );
}

// ── Groups list panel ───────────────────────────────────────────────────────

export function GroupsPanel(): JSX.Element {
  const { groups, createGroup, updateGroup, deleteGroup } = useGroups();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const selected = selectedId
    ? groups.find((g) => g.id === selectedId)
    : null;

  if (selected) {
    return (
      <GroupDetail
        group={selected}
        onBack={() => { setSelectedId(null); }}
        onUpdate={updateGroup}
        onDelete={deleteGroup}
      />
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleCreate = async () => {
    setCreating(true);
    try {
      const g = await createGroup("New Group");
      setSelectedId(g.id);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="px-6 py-5 space-y-3">
      <PageSectionHeader
        title="Groups"
        description="Organize entities into groups with shared rules and identity."
        actions={
          <button
            type="button"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={handleCreate}
            disabled={creating}
            className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer hover:bg-surface-hover disabled:opacity-50"
          >
            <Icon name="plus" size={18} className="text-content-secondary" />
          </button>
        }
      />

      {groups.length === 0 ? (
        <div className="py-8 text-center text-content-muted text-sm">
          No groups yet.
        </div>
      ) : (
        <div className="rounded-xl bg-surface-tertiary border border-edge overflow-hidden divide-y divide-edge">
          {groups.map((g) => (
            <GroupRow
              key={g.id}
              group={g}
              onClick={() => { setSelectedId(g.id); }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
